import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function PlatformFeatures() {
  const [openIndex, setOpenIndex] = useState<number>(0);
  const features = [{
    title: 'MCP-native integration.',
    desc: 'Connect any AI agent through the Model Context Protocol. No custom adapters, no middleware.'
  }, {
    title: 'Semantic data layer.',
    desc: 'Raw clinical data is normalized into structured context agents can reason over.'
  }, {
    title: 'Read and write capable.',
    desc: 'Agents can query patient data and take actions: submit prior auths, update records, create orders.'
  }, {
    title: 'Compliance built in.',
    desc: 'HIPAA-compliant infrastructure with access controls, audit trails, and schema validation from day one.'
  }];
  return <section id="platform" className="py-24 bg-hubble-card/30 relative z-10 border-y border-hubble-border/50" data-id="element-75">
      <div className="max-w-7xl mx-auto px-6" data-id="element-76">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-77">
          {/* Left Column: Accordion */}
          <div data-id="element-78">
            <span className="text-hubble-accent text-[12px] md:text-[14px] font-body font-medium tracking-[3px] uppercase mb-4 block" data-id="element-79">
              PLATFORM
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-normal tracking-[-1px] text-hubble-text mb-12" data-id="element-80">
              Built for agents. Not just APIs.
            </h2>

            <div className="space-y-4" data-id="element-81">
              {features.map((feature, index) => <div key={index} className={`border-b border-hubble-border pb-4 transition-colors ${openIndex === index ? 'border-hubble-primary/50' : ''}`} data-id="element-82">
                  <button onClick={() => setOpenIndex(openIndex === index ? -1 : index)} className="w-full flex items-center justify-between text-left py-4 focus:outline-none group" data-id="element-83">
                    <h3 className={`font-heading text-xl font-medium transition-colors ${openIndex === index ? 'text-hubble-accent' : 'text-hubble-text group-hover:text-hubble-body'}`} data-id="element-84">
                      {feature.title}
                    </h3>
                    <ChevronDown className={`text-hubble-body transition-transform duration-300 ${openIndex === index ? 'rotate-180 text-hubble-accent' : ''}`} data-id="element-85" />
                  </button>
                  <AnimatePresence data-id="element-86">
                    {openIndex === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-87">
                        <p className="font-body text-[16px] text-hubble-body pb-4 leading-[1.6]" data-id="element-88">
                          {feature.desc}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>

          {/* Right Column: Animated Graphic */}
          <div className="relative h-[500px] w-full flex items-center justify-center rounded-2xl bg-hubble-bg border border-hubble-border overflow-hidden" data-id="element-89">
            

            <svg viewBox="0 0 400 400" className="w-full h-full max-w-[400px]" data-id="element-90">
              <defs data-id="element-91">
                <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-92">
                  <stop offset="0%" stopColor="#0D7C6B" stopOpacity="0.8" data-id="element-93" />
                  <stop offset="100%" stopColor="#3DB89E" stopOpacity="0.2" data-id="element-94" />
                </linearGradient>
                <filter id="blur" x="-20%" y="-20%" width="140%" height="140%" data-id="element-95">
                  <feGaussianBlur stdDeviation="4" data-id="element-96" />
                </filter>
              </defs>

              {/* Orbital Rings */}
              <motion.g animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} transform-origin="200 200" data-id="element-97">
                <ellipse cx="200" cy="200" rx="140" ry="60" fill="none" stroke="#1E2A36" strokeWidth="1" opacity="0.5" transform="rotate(30 200 200)" data-id="element-98" />
                <circle cx="80" cy="130" r="4" fill="#3DB89E" filter="url(#blur)" data-id="element-99" />
                <circle cx="320" cy="270" r="3" fill="#0A5C50" data-id="element-100" />
              </motion.g>

              <motion.g animate={{
              rotate: -360
            }} transition={{
              duration: 50,
              repeat: Infinity,
              ease: 'linear'
            }} transform-origin="200 200" data-id="element-101">
                <ellipse cx="200" cy="200" rx="160" ry="80" fill="none" stroke="#1E2A36" strokeWidth="1" transform="rotate(-45 200 200)" data-id="element-102" />
                <circle cx="310" cy="120" r="5" fill="#0D7C6B" filter="url(#blur)" data-id="element-103" />
              </motion.g>

              {/* Connected Nodes */}
              <g data-id="element-104">
                <line x1="200" y1="200" x2="120" y2="120" stroke="#0A5C50" strokeWidth="1" strokeDasharray="4 4" data-id="element-105" />
                <line x1="200" y1="200" x2="280" y2="100" stroke="#0A5C50" strokeWidth="1" strokeDasharray="4 4" data-id="element-106" />
                <line x1="200" y1="200" x2="100" y2="250" stroke="#0A5C50" strokeWidth="1" strokeDasharray="4 4" data-id="element-107" />
                <line x1="200" y1="200" x2="300" y2="280" stroke="#0A5C50" strokeWidth="1" strokeDasharray="4 4" data-id="element-108" />

                <motion.circle animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 3,
                repeat: Infinity
              }} cx="120" cy="120" r="8" fill="#121A22" stroke="#0D7C6B" strokeWidth="2" data-id="element-109" />
                <motion.circle animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                delay: 0.5
              }} cx="280" cy="100" r="6" fill="#121A22" stroke="#3DB89E" strokeWidth="2" data-id="element-110" />
                <motion.circle animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                delay: 1
              }} cx="100" cy="250" r="10" fill="#121A22" stroke="#0D7C6B" strokeWidth="2" data-id="element-111" />
                <motion.circle animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                delay: 1.5
              }} cx="300" cy="280" r="7" fill="#121A22" stroke="#0A5C50" strokeWidth="2" data-id="element-112" />

                <circle cx="200" cy="200" r="24" fill="url(#glow)" filter="url(#blur)" data-id="element-113" />
                <circle cx="200" cy="200" r="16" fill="#0A0F14" stroke="#3DB89E" strokeWidth="3" data-id="element-114" />
                <circle cx="200" cy="200" r="6" fill="#FFFFFF" data-id="element-115" />
              </g>
            </svg>
          </div>
        </div>
      </div>
    </section>;
}